// Dialog for creating a new webhook. Two phases:
//   1. Form: name, source, signing toggle.
//   2. Receipt: show the full URL + secret ONCE so the user can store them.
//      We mask the secret in every other API response forever after this.

import { Dialog } from "@base-ui-components/react/dialog";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Check, Copy, Loader2, X } from "lucide-react";
import { useState } from "react";

import { Pill } from "@/components/glass";

interface CreateProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	projectId: string;
}

interface CreatedWebhook {
	id: string;
	projectId: string;
	name: string;
	slug: string;
	secret: string | null;
	sourceName: string;
	mode: string;
}

async function createWebhook(
	projectId: string,
	body: {
		name: string;
		sourceName: string;
		mode: string;
		signed: boolean;
	},
): Promise<CreatedWebhook> {
	const r = await fetch(`/api/projects/${projectId}/webhooks`, {
		method: "POST",
		credentials: "include",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify(body),
	});
	if (!r.ok) {
		const e = await r.json().catch(() => ({}));
		throw new Error((e as { error?: string }).error || `create: ${r.status}`);
	}
	const json = (await r.json()) as { webhook: CreatedWebhook };
	return json.webhook;
}

export function WebhookCreateDialog({ open, onOpenChange, projectId }: CreateProps) {
	const qc = useQueryClient();
	const [name, setName] = useState("");
	const [sourceName, setSourceName] = useState("");
	const [signed, setSigned] = useState(true);
	const [created, setCreated] = useState<CreatedWebhook | null>(null);

	const mutation = useMutation({
		mutationFn: () =>
			createWebhook(projectId, {
				name: name.trim(),
				sourceName: sourceName.trim() || slugify(name.trim()),
				mode: "raw",
				signed,
			}),
		onSuccess: (wh) => {
			setCreated(wh);
			qc.invalidateQueries({ queryKey: ["webhooks", projectId] });
		},
	});

	const close = () => {
		onOpenChange(false);
		setTimeout(() => {
			setName("");
			setSourceName("");
			setSigned(true);
			setCreated(null);
			mutation.reset();
		}, 200);
	};

	const url = created
		? `${window.location.origin}/api/hooks/${created.slug}`
		: "";

	return (
		<Dialog.Root open={open} onOpenChange={(o) => (o ? onOpenChange(true) : close())}>
			<Dialog.Portal>
				<Dialog.Backdrop className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm" />
				<Dialog.Popup
					className="fixed left-1/2 top-1/2 z-50 -translate-x-1/2 -translate-y-1/2 w-[480px] max-w-[calc(100vw-32px)] rounded-[var(--radius-panel)] p-5"
					style={{
						background: "rgb(20 20 23 / 0.98)",
						backdropFilter: "blur(20px) saturate(180%)",
						WebkitBackdropFilter: "blur(20px) saturate(180%)",
						border: "1px solid rgb(255 255 255 / 0.1)",
						boxShadow: "0 24px 60px rgba(0,0,0,0.6), inset 0 1px 0 rgba(255,255,255,0.06)",
					}}
				>
					<div className="flex items-center justify-between mb-4">
						<Dialog.Title className="text-[14px] font-medium">
							{created ? "Webhook created" : "New webhook"}
						</Dialog.Title>
						<Dialog.Close className="text-[var(--color-text-dim)] hover:text-[var(--color-text)] transition-colors">
							<X className="w-4 h-4" />
						</Dialog.Close>
					</div>

					{!created ? (
						<form
							onSubmit={(e) => {
								e.preventDefault();
								if (!name.trim()) return;
								mutation.mutate();
							}}
							className="space-y-3"
						>
							<Labeled label="Name">
								<input
									type="text"
									value={name}
									onChange={(e) => setName(e.target.value)}
									placeholder="GitHub events"
									autoFocus
									autoComplete="off"
									spellCheck={false}
									className="w-full h-9 px-3 rounded-[var(--radius-pill)] bg-[var(--color-surface-solid)] border border-[var(--color-border)] text-[13px] outline-none focus:border-[var(--color-border-hi)]"
								/>
							</Labeled>
							<Labeled
								label="Source name"
								hint="Ingested nodes will carry this as their source field."
							>
								<input
									type="text"
									value={sourceName}
									onChange={(e) => setSourceName(e.target.value)}
									placeholder={name.trim() ? slugify(name.trim()) : "github"}
									autoComplete="off"
									spellCheck={false}
									className="w-full h-9 px-3 rounded-[var(--radius-pill)] bg-[var(--color-surface-solid)] border border-[var(--color-border)] mono text-[13px] outline-none focus:border-[var(--color-border-hi)]"
								/>
							</Labeled>
							<Labeled label="Signing">
								<label className="flex items-center gap-2 text-[12px] text-[var(--color-text-muted)] cursor-pointer">
									<input
										type="checkbox"
										checked={signed}
										onChange={(e) => setSigned(e.target.checked)}
										className="accent-white"
									/>
									Sign with HMAC-SHA256 (recommended)
								</label>
							</Labeled>
							{mutation.isError && (
								<div className="text-[12px] text-[var(--color-danger)]">
									{(mutation.error as Error).message}
								</div>
							)}
							<div className="flex justify-end gap-2 pt-1">
								<Dialog.Close render={<Pill>Cancel</Pill>} />
								<Pill type="submit" disabled={!name.trim() || mutation.isPending}>
									{mutation.isPending ? (
										<Loader2 className="w-3.5 h-3.5 animate-spin" />
									) : null}
									Create
								</Pill>
							</div>
						</form>
					) : (
						<div className="space-y-4">
							<div className="text-[12px] text-[var(--color-text-muted)]">
								Save these now. The secret won't be shown again. (You can rotate later.)
							</div>
							<CopyableField label="Webhook URL" value={url} />
							{created.secret && (
								<CopyableField label="Signing secret" value={created.secret} mono />
							)}
							<div className="text-[11px] text-[var(--color-text-dim)]">
								Configure the sender to POST JSON to the URL above, with the secret as{" "}
								<span className="mono">X-Renketsu-Signature: sha256=&lt;hex&gt;</span>{" "}
								(HMAC-SHA256 of the body).
							</div>
							<div className="flex justify-end">
								<Pill onClick={close}>Done</Pill>
							</div>
						</div>
					)}
				</Dialog.Popup>
			</Dialog.Portal>
		</Dialog.Root>
	);
}

function Labeled({
	label,
	hint,
	children,
}: { label: string; hint?: string; children: React.ReactNode }) {
	return (
		<div>
			<div className="text-[10px] uppercase tracking-wider text-[var(--color-text-dim)] mb-1">
				{label}
			</div>
			{children}
			{hint && (
				<div className="text-[11px] text-[var(--color-text-dim)] mt-1">{hint}</div>
			)}
		</div>
	);
}

function CopyableField({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
	const [copied, setCopied] = useState(false);
	const copy = async () => {
		try {
			await navigator.clipboard.writeText(value);
			setCopied(true);
			setTimeout(() => setCopied(false), 1200);
		} catch {
			// silent
		}
	};
	return (
		<div>
			<div className="text-[10px] uppercase tracking-wider text-[var(--color-text-dim)] mb-1">
				{label}
			</div>
			<div className="flex items-center gap-2 px-3 py-2 rounded-md bg-black/30 border border-[var(--color-border)]">
				<div
					className={`flex-1 min-w-0 truncate text-[12px] ${mono ? "mono" : ""}`}
				>
					{value}
				</div>
				<button
					type="button"
					onClick={copy}
					className="text-[var(--color-text-dim)] hover:text-[var(--color-text)] shrink-0"
					aria-label="Copy"
				>
					{copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
				</button>
			</div>
		</div>
	);
}

function slugify(s: string): string {
	return s
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, "-")
		.replace(/^-+|-+$/g, "")
		.slice(0, 64);
}
